/**
 * Follow-up billing-reality UI: the advisory enrollment banner and the
 * clinician's billing-track selector.
 *
 * The follow-up counterpart to {@link IntakeBillingRealitySection}. The
 * difference is what the advisory says: intake affirms an eligibility
 * determination made at scheduling, whereas a follow-up member is already
 * enrolled, so the banner names the enrollment and the selector asks the
 * clinician to affirm the track it implies.
 *
 * The labels here are UI-only ("CoCM Followup", "Wellness Followup", …) — the
 * underlying encounter-type codes and their stored displays are unchanged.
 * @agentic connections
 */

import { Alert } from "@mantine/core";
import { IconClipboardCheck } from "@tabler/icons-react";
import { CardSelect } from "@benchmark/ui/components/atomic";

/**
 * Which enrollment the advisory banner reports. `undefined` means the
 * member's care plans have not loaded yet.
 * @agentic connections
 */
export type FollowUpEnrollment = "cocm" | "triage" | "none" | undefined;

/**
 * Billing-track options shown in the follow-up selector. Values are the three
 * follow-up encounter-type codes; labels/descriptions are UI-only.
 *
 * The descriptions deliberately make no claim about which minutes count toward
 * CoCM. Unlike intake — where billing a Wellness/90791 exam creates a ChargeItem
 * that carves pre-enrollment minutes out of the CoCM month — a followup creates
 * no charge, and CoCM billable minutes are windowed by the member's CoCM
 * episode, not filtered by encounter type or care-time method. Saying otherwise
 * would tell clinicians their track choice gates CoCM time when it does not.
 * @agentic connections
 */
export const FOLLOW_UP_BILLING_REALITY_OPTIONS = [
  {
    value: "cocm-follow-up-interaction",
    label: "CoCM Followup",
    description:
      "Collaborative Care followup. Reported as CoCM work, and the logged time is attributed to the member's CoCM follow-up care.",
  },
  {
    value: "clinical-follow-up-interaction",
    label: "Wellness Followup",
    description:
      "Followup on the Wellness (90791) track. Reported as Wellness work; a followup never creates its own charge.",
  },
  {
    value: "unbilled-follow-up-interaction",
    label: "Non-Billable Followup",
    description:
      "Documented but not reported as billable work — use when the member's followup is outside a billable track.",
  },
] as const;

const ENROLLMENT_BANNER_MESSAGE: Record<
  NonNullable<FollowUpEnrollment>,
  string
> = {
  cocm: "Member is actively enrolled in the CoCM care plan.",
  triage: "Member is enrolled in Triage. No active CoCM care plan.",
  none: "Member has no active Triage or CoCM care plan.",
};

/**
 * Advisory banner surfacing the member's active care plan enrollment.
 * Read-only — it informs, but does not constrain, the clinician's track
 * choice. Shares the blue/gray accent scheme with
 * {@link FollowUpBillingRealitySelect} so the two follow-up billing surfaces
 * read as one system (and match the intake pair).
 * @agentic connections
 */
export const FollowUpEnrollmentBanner = ({
  enrollment,
}: {
  enrollment: FollowUpEnrollment;
}) => (
  <Alert
    variant="light"
    color={enrollment === "cocm" ? "blue" : "gray"}
    icon={<IconClipboardCheck size={18} />}
    title="Enrollment (from care plan)"
    data-testid="follow-up-enrollment-indicator"
  >
    {enrollment
      ? ENROLLMENT_BANNER_MESSAGE[enrollment]
      : "Checking the member's active care plans…"}
  </Alert>
);

/**
 * The clinician's follow-up billing-track selector (prefilled from the
 * member's active care plan, falling back to their intake track).
 * @agentic connections
 */
export const FollowUpBillingRealitySelect = ({
  value,
  readOnly = false,
  onChange,
}: {
  value: string;
  readOnly?: boolean;
  onChange: (code: string) => void;
}) => (
  <CardSelect
    data={FOLLOW_UP_BILLING_REALITY_OPTIONS.map((o) => ({
      value: o.value,
      label: o.label,
      description: o.description,
    }))}
    value={value || null}
    onChange={(v) => {
      if (readOnly) return;
      const code = Array.isArray(v) ? v[0] : v;
      if (code) onChange(code);
    }}
    columns={3}
    compact
    aria-label="Select how this followup is billed"
  />
);
